// --- React ---
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import Loading from "../../../components/loading/loading";
import FormInput from "../../../components/form-input/FormInput";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";
import ButtonGoback from "../../../components/button/buttonGoback";
import { useLookupParameter } from "../../../utils/get-lookup";
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { useResponsive } from "../../../hooks/mediaQuery";
// --- Utils ---
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useUnsavedChangesWarning } from "../../../hooks/useUnsavedChangesWarning";
import { LoaderCircle } from "lucide-react";
import { UrlCollection } from "../../../common/url-collection";
import dayjs from "dayjs";
// --- Models ---
import { standardSchema } from "../../../models/schema/standard";
import { StandardItem, ParameterStandardItem, parameterStandardViews } from "../../../models/types/standard";

// --- Services ---
import standardSvc from "../../../services/api/standard";
import FormDate from "../../../components/form-input/formDate";
import { useTranslation } from "react-i18next";

interface FormProps {
  isCreate: boolean;
  data: StandardItem | null;
}

export default function StandardCreateUpdate({ isCreate, data }: FormProps) {
  const navigate = useNavigate();
  const today = dayjs();
  const { lookupParameter } = useLookupParameter();
  const { isXs } = useResponsive();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [comfirmDelete, setComfirmDelete] = useState({
    isOpen: false,
    index: -1,
  });

  const schema = standardSchema;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      parameterStandards: [],
      issuedDate: '',
      effectiveDate: '',
      endDate: '',
    },
  });

  useEffect(() => {
    if (data) {
      let parameterStandards: ParameterStandardItem[] = [];
      if (data.parameterStandardViews && data.parameterStandardViews.length > 0) {
        parameterStandards = data.parameterStandardViews.map((item: parameterStandardViews) => {
          return {
            standardId: item.standardId,
            parameterId: item.parameterId,
            standardA: item.standardA?.toString() || null,
            standardB: item.standardB?.toString() || null,
            standardC: item.standardC?.toString() || null,
          }
        })
      }
      reset({
        parameterStandards: parameterStandards.map(item => ({
          ...item,
          standardA: item.standardA || undefined,
          standardB: item.standardB || undefined,
          standardC: item.standardC || undefined
        })),
        ...omitMetaFields(data, ['parameterStandardViews', 'isDelete']),
      })
    }
  }, [data]);

  const { showModal, confirmLeave, cancelLeave, confirmContent, unlock } = useUnsavedChangesWarning(isDirty);

  const onSubmit = async (values: any) => {
    setLoading(true);
    try {
      const res = isCreate ? await standardSvc.createStandard(values) : await standardSvc.updateStandard({ ...values, id: Number(data?.id) });
      if (res && res.content.status) {
        unlock()
        notify(t('common.success'), isCreate ? t('standard.notifications.createSuccess') : t('standard.notifications.updateSuccess'), "success");
        setLoading(false);
        navigate(UrlCollection.Standard);
      }
    } catch (e: any) {
      notify(t('common.error'), t('standard.notifications.error'), "danger");
    } finally {
      setLoading(false);
    }
  };


  const handleAddParameter = () => {
    let newParameter: any = {
      parameterId: null,
      standardA: null,
      standardB: null,
      standardC: null,
    };
    setValue('parameterStandards', [...watch('parameterStandards'), newParameter]);
  };

  const handleValueChange = (index: number, newValue: any) => {
    const updatedParameters = watch('parameterStandards').map((param, idx) =>
      idx === index ? newValue : param
    );
    // console.log(updatedParameters);
    setValue('parameterStandards', updatedParameters, { shouldDirty: true });
  };

  const handleRemoveParameter = (index: number) => {
    const updatedParameters = watch('parameterStandards').filter((_, i) => i !== index);
    setComfirmDelete({ isOpen: false, index: -1 });
    setValue('parameterStandards', updatedParameters, { shouldDirty: true });
  };

  return (
    <>
      {showModal && (
        <ModalConfirm
          isOpen={showModal}
          onClose={cancelLeave}
          title={confirmContent.title}
          content={confirmContent.content}
          titleComfirm={t('common.leave')}
          action={confirmLeave}
        />
      )}
      {comfirmDelete.isOpen && (
        <ModalConfirm
          isOpen={comfirmDelete.isOpen}
          onClose={() => setComfirmDelete({ isOpen: false, index: -1 })}
          title="Xác nhận xóa"
          content="Bạn có chắc chắn muốn xóa thông số này?"
          titleComfirm="Xóa"
          action={() => handleRemoveParameter(comfirmDelete.index)}
        />
      )}
      <form className="w-full h-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-center mb-4 sticky top-0 z-10 bg-gray-100">
          <ButtonGoback />
          <button
            disabled={!isDirty}
            type="submit"
            className="btn_submit shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <div className="flex items-center justify-center gap-2">
              {loading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div> : ""}
              {isCreate ? t('standard.form.buttons.create') : t('standard.form.buttons.update')}
            </div>
          </button>
        </div>
        <div>
          <div className={`max-w-[1000px] mx-auto ${isXs ? 'p-4' : 'p-8'} bg-white rounded-lg shadow my-2`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <FormInput
                label={t('standard.form.fields.name')}
                name="name"
                register={register("name")}
                error={errors.name}
                required
              />
              <FormInput
                label={t('standard.form.fields.description')}
                name="description"
                register={register("description")}
                error={errors.description}
              />
              <FormDate
                label={t('standard.form.fields.effectiveDate')}
                name="effectiveDate"
                control={control}
                errors={errors}
                outputFormat="YYYY-MM-DD"
              />
              <FormDate
                label={t('standard.form.fields.endDate')}
                name="endDate"
                control={control}
                outputFormat="YYYY-MM-DD"
                errors={errors}
              />
              <FormDate
                label={t('standard.form.fields.issuedDate')}
                name="issuedDate"
                control={control}
                outputFormat="YYYY-MM-DD"
                errors={errors}
                maxDate={today.add(1, 'year')}
              />
            </div>
          </div>
          <div className={`${isXs ? 'p-0' : 'p-4'}`}>
            <button
              disabled={!watch("name")}
              type="button"
              className="btn_submit mb-2"
              onClick={handleAddParameter}
            >
              {t('standard.form.buttons.addParameter')}
            </button>
            <div className="relative overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
              <table className="min-w-full text-sm text-left text-gray-700">
                <thead className="bg-gray-200 text-sm font-semibold uppercase text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left w-[50px]">{t('standard.table.stt')}</th>
                    <th className="px-4 py-3 text-left">{t('standard.table.code')}</th>
                    <th className="px-4 py-3 text-left min-w-[240px]">{t('standard.table.parameter')}</th>
                    <th className="px-4 py-3 text-left">{t('standard.table.unit')}</th>
                    <th className="px-4 py-3 text-left min-w-[120px]">{t('standard.table.standardA')}</th>
                    <th className="px-4 py-3 text-left min-w-[120px]">{t('standard.table.standardB')}</th>
                    <th className="px-4 py-3 text-left min-w-[120px]">{t('standard.table.standardC')}</th>
                    <th className="px-4 py-3 text-left w-[100px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {watch("parameterStandards")?.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-4 text-center text-gray-500">
                        {t('standard.form.parameterTable.noData')}
                      </td>
                    </tr>
                  ) :
                    watch("parameterStandards").map((parameter, index) => {
                      const selectedIds = watch("parameterStandards")
                        .filter((_, idx) => idx !== index)
                        .map(p => p.parameterId);

                      const availableOptions = lookupParameter.filter(
                        (item) => !selectedIds.includes(item.value) || item.value === parameter.parameterId
                      );

                      const unit = lookupParameter.find(p => p.value === parameter.parameterId)?.unitName;
                      const code = lookupParameter.find(p => p.value === parameter.parameterId)?.code;
                      return (
                        <tr key={index} className={`hover:bg-gray-100 ${index % 2 === 1 ? "bg-gray-50" : ""}`}>
                          <td className="px-4 py-3 text-left">{index + 1}</td>
                          <td className="px-4 py-3 text-left">
                            {code || "-"}
                          </td>
                          <td className="px-4 py-3 text-left">
                            <Select
                              fullWidth
                              size="small"
                              value={parameter?.parameterId ?? ""}
                              onChange={(e: any) => handleValueChange(index, { ...parameter, parameterId: e.target.value })}
                              displayEmpty
                            >
                              <MenuItem value="" disabled>Chọn tham số</MenuItem>
                              {availableOptions.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                  {item.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors?.parameterStandards?.[index]?.parameterId && (
                              <span className="text-red-500 text-xs mt-1 block">
                                {errors?.parameterStandards?.[index]?.parameterId?.message}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-left">
                            {unit || "-"}
                          </td>
                          <td className="px-4 py-3 text-left">
                            <input
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                              type="text"
                              inputMode="decimal"
                              value={parameter?.standardA === null || parameter?.standardA === undefined ? '' : parameter?.standardA}
                              onChange={(e) => {
                                const value = e.target.value.replace(/,/g, '');
                                handleValueChange(index, { ...parameter, standardA: value });
                              }}
                            />
                            {errors?.parameterStandards?.[index]?.standardA && (
                              <span className="text-red-500 text-xs mt-1 block">
                                {errors?.parameterStandards?.[index]?.standardA?.message}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-left">
                            <input
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                              type="text"
                              inputMode="decimal"
                              value={parameter?.standardB === null || parameter?.standardB === undefined ? '' : parameter?.standardB}
                              onChange={(e) => {
                                const value = e.target.value.replace(/,/g, '');
                                handleValueChange(index, { ...parameter, standardB: value || null });
                              }}
                            />

                            {errors?.parameterStandards?.[index]?.standardB && (
                              <span className="text-red-500 text-xs mt-1 block">
                                {errors?.parameterStandards?.[index]?.standardB?.message}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-left">
                            <input
                              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                              type="text"
                              inputMode="decimal"
                              value={parameter?.standardC === null || parameter?.standardC === undefined ? '' : parameter?.standardC}
                              onChange={(e) => {
                                const value = e.target.value.replace(/,/g, '');
                                handleValueChange(index, { ...parameter, standardC: value || null });
                              }}
                            />
                            {errors?.parameterStandards?.[index]?.standardC && (
                              <span className="text-red-500 text-xs mt-1 block">
                                {errors?.parameterStandards?.[index]?.standardC?.message}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-left">
                            <button
                              type="button"
                              className="btn bg-red-500 text-white"
                              onClick={() => setComfirmDelete({ isOpen: true, index: index })}
                            >
                              {t('standard.form.buttons.delete')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}